import React, { useMemo, useRef } from 'react';
import { Label } from 'reactstrap';
import LoginFieldError from '../LoginFieldError';

const LENGTH = 6;

const OtpCodeFields = ({
  id = 'mfa-otp',
  label = 'Authentication code',
  value = '',
  onChange,
  error,
  autoFocus = false,
  disabled = false,
}) => {
  const inputsRef = useRef([]);
  const digits = useMemo(() => {
    const chars = String(value).replace(/\D/g, '').slice(0, LENGTH).split('');
    return Array.from({ length: LENGTH }, (_, i) => chars[i] || '');
  }, [value]);

  const emit = nextDigits => {
    onChange?.(nextDigits.join('').slice(0, LENGTH));
  };

  const focusAt = index => {
    const el = inputsRef.current[index];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const handleChange = (index, raw) => {
    const cleaned = raw.replace(/\D/g, '');
    if (!cleaned) {
      const next = [...digits];
      next[index] = '';
      emit(next);
      return;
    }

    const next = [...digits];
    const chars = cleaned.slice(0, LENGTH - index).split('');
    chars.forEach((char, offset) => {
      next[index + offset] = char;
    });
    emit(next);
    focusAt(Math.min(index + chars.length, LENGTH - 1));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const next = [...digits];
        next[index] = '';
        emit(next);
        return;
      }
      if (index > 0) {
        e.preventDefault();
        const next = [...digits];
        next[index - 1] = '';
        emit(next);
        focusAt(index - 1);
      }
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusAt(index - 1);
    }
    if (e.key === 'ArrowRight' && index < LENGTH - 1) {
      e.preventDefault();
      focusAt(index + 1);
    }
  };

  const handlePaste = e => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '')
      .replace(/\D/g, '')
      .slice(0, LENGTH);
    if (!pasted) return;
    const next = Array.from({ length: LENGTH }, (_, i) => pasted[i] || '');
    emit(next);
    focusAt(Math.min(pasted.length, LENGTH) - 1);
  };

  return (
    <div className='mb-3'>
      <Label id={`${id}-label`} className='login-label' htmlFor={id}>
        {label}
        <span className='text-danger'> *</span>
        <span className='visually-hidden'> (required)</span>
      </Label>
      <div
        className={`mfa-otp${error ? ' is-invalid' : ''}`}
        role='group'
        aria-labelledby={`${id}-label`}
        aria-required='true'
        onPaste={handlePaste}
      >
        {digits.map((digit, index) => (
          <input
            key={`${id}-${index}`}
            ref={el => {
              inputsRef.current[index] = el;
            }}
            id={index === 0 ? id : `${id}-${index}`}
            type='text'
            inputMode='numeric'
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={LENGTH}
            className={`mfa-otp__cell${error ? ' is-invalid' : ''}`}
            value={digit}
            disabled={disabled}
            autoFocus={autoFocus && index === 0}
            aria-label={`Digit ${index + 1} of ${LENGTH}`}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${id}-error` : undefined}
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onFocus={e => e.target.select()}
          />
        ))}
      </div>
      <LoginFieldError id={`${id}-error`} message={error} />
    </div>
  );
};

export default OtpCodeFields;
